/* eslint-disable react-hooks/exhaustive-deps */

import React, {useContext, useEffect, useMemo} from 'react';
import {
  blueCheckMark,
  imageDetect,
  imageMatchBlue,
} from '../../../assets/images/images';
import {Card, Wrapper} from '../FraudCheckScreens';
import {EasyOnboardContext} from '../../../helpers/context';
import {useDispatch, useSelector} from 'react-redux';
import {setWidgetPages} from '../../../store/actions';
import {useFraudCheck} from '../../../hooks/useFraudCheck';

import {CustomRangeSlider} from '../../../components/Sliders/CustomRangeSlider';

const Slider = ({setRangeValue, rangeValue}) => (
  <div className="relative !mt-8 w-[97%] mx-auto flex items-center">
    <CustomRangeSlider
      value={rangeValue}
      onChange={setRangeValue}
      className="w-full h-4"
      colors={{
        thumbs: {
          first: ' bg-status-404',
          middle: 'bg-warning',
          last: 'bg-success',
        },
        tooltips: {
          first: ' bg-status-404',
          middle: 'bg-warning',
          last: 'bg-warning',
        },
        tracks: {
          first: ' bg-status-404',
          middle: 'bg-warning',
          last: 'bg-success',
          single: 'bg-gray-300',
        },
        pointers: {
          first: 'after:border-b-status-404',
          last: 'after:border-b-warning',
        },
      }}
    />
  </div>
);
const statuses = [
  {
    text: 'Failed',
    bg: 'bg-status-404',
  },
  {
    text: 'Pending',
    bg: 'bg-warning',
  },
  {
    text: 'Succeeded',
    bg: 'bg-success',
  },
];

export const DetectWithLivenessMatch = () => {
  const {widgetPages} = useSelector(state => state?.widgets);
  const dispatch = useDispatch();
  const {detectWithLiveness, setDetectWithLiveness} =
    useContext(EasyOnboardContext);

  const foundLivenessOrSelfie = widgetPages?.filter(
    page =>
      page?.page === 'selfie' ||
      (page?.page === 'government-data' && page?.config?.selfie === true),
  );

  const {setConfig, config} = useFraudCheck({
    image_match: detectWithLiveness?.image_match || {
      failed: [0, 24],
      pending: [25, 65],
      successful: [66, 100],
    },
    liveness_score: detectWithLiveness?.liveness_score || {
      failed: [0, 24],
      pending: [25, 65],
      successful: [66, 100],
    },
  });

  useEffect(() => {
    setDetectWithLiveness({...config});
  }, [config, setDetectWithLiveness]);

  useEffect(() => {
    if (foundLivenessOrSelfie.length > 0) {
      foundLivenessOrSelfie.map(page => {
        const updatedConfig = {
          brightnessThreshold:
            detectWithLiveness?.brightnessThreshold ||
            config?.brightnessThreshold,
          glassesCheck:
            detectWithLiveness?.glassesCheck || config?.glassesCheck,
        };
        if (page?.page === 'selfie') {
          dispatch(
            setWidgetPages({
              ...page,
              config: {
                ...page?.config,
                ...updatedConfig,
              },
            }),
          );
        }
        if (page?.page === 'government-data') {
          dispatch(
            setWidgetPages({
              ...page,
              config: {
                ...page?.config,
                ...updatedConfig,
              },
            }),
          );
        }
      });
    }
  }, [config]);

  const updateConfig = useMemo(() => {
    const {liveness_score = {}, image_match = {}} = config || {};
    if (!liveness_score.failed?.length && !image_match.failed?.length) {
      return {imageMatch: [25, 65], livenessScore: [25, 65]};
    }

    return {
      livenessScore: [
        liveness_score.failed?.[1] ?? 24,
        liveness_score.pending?.[1] ?? 65,
      ],
      imageMatch: [
        image_match.failed?.[1] ?? 24,
        image_match.pending?.[1] ?? 65,
      ],
    };
  }, [config]);

  return (
    <Wrapper>
      <Card
        src={imageDetect}
        extension={
          <div className="flex flex-col gap-1">
            <p className="text-grey font-medium">Detect with Liveness</p>
            <p className="text-body text-sm gap-1">
              Set strictness threshold for liveness checks.{' '}
              <span>
                <a
                  href="https://docs.google.com/document/d/1gA_afSyaOYsHwvG8IA1ecPRPuh0zQbsoQ6QKByD-Lbw/edit?usp=sharing"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-brandBlue font-medium"
                >
                  Get help on threshold adjustment.
                </a>
              </span>
            </p>

            <div className="flex items-center gap-2 mt-2">
              {statuses.map(status => (
                <div key={status.text} className={`flex items-center gap-1`}>
                  <div className={`h-3 w-3 rounded-full ${status.bg}`}></div>
                  <p className="text-body text-sm">{status.text}</p>
                </div>
              ))}
            </div>
          </div>
        }
      />

      <Card
        extension={
          <div className="flex flex-col gap-1">
            <p className="text-grey font-medium">Liveness score</p>
            <p className="text-body text-sm gap-1">
              Set how strict real-time presence is verified during selfie
              capture
            </p>
          </div>
        }
        src={blueCheckMark}
      >
        <Slider
          rangeValue={updateConfig?.livenessScore}
          setRangeValue={range => {
            const firstValue = range[0];
            const secondValue = range[1];
            setConfig({
              ...config,
              liveness_score: {
                failed: [0, firstValue],
                pending: [firstValue + 1, secondValue],
                successful: [secondValue + 1, 100],
              },
            });
          }}
        />
      </Card>
      <Card
        extension={
          <div className="flex flex-col gap-1">
            <p className="text-grey font-medium">Image match</p>
            <p className="text-body text-sm gap-1">
              Set how strict live selfies are matched with ID image
            </p>
          </div>
        }
        src={imageMatchBlue}
      >
        <Slider
          rangeValue={updateConfig?.imageMatch}
          setRangeValue={range => {
            const firstValue = range[0];
            const secondValue = range[1];
            setConfig({
              ...config,
              image_match: {
                failed: [0, firstValue],
                pending: [firstValue + 1, secondValue],
                successful: [secondValue + 1, 100],
              },
            });
          }}
        />
      </Card>
      {/*<Card*/}
      {/*  extension={*/}
      {/*    <p className="text-grey font-medium">*/}
      {/*      If liveness configuration does not match*/}
      {/*    </p>*/}
      {/*  }*/}
      {/*  src={userRoundCheckRed}*/}
      {/*>*/}
      {/*  <div className="text-start w-full">*/}
      {/*    <label htmlFor="status" className="mb-2 relative">*/}
      {/*      Set status to*/}
      {/*    </label>*/}
      {/*    {actionReturned !== undefined && (*/}
      {/*      <Select*/}
      {/*        defaultValue={actionReturned}*/}
      {/*        value={actionReturned || STATUSES[0]}*/}
      {/*        onChange={status => {*/}
      {/*          setSelectedStatus(status);*/}
      {/*          setConfig({...config, action: status?.value});*/}
      {/*        }}*/}
      {/*        options={STATUSES}*/}
      {/*        className="basic-multi-select"*/}
      {/*        classNamePrefix="react-select"*/}
      {/*        components={{DropdownIndicator}}*/}
      {/*        styles={customStyles}*/}
      {/*        menuPlacement="auto"*/}
      {/*        isSearchable={false}*/}
      {/*      />*/}
      {/*    )}*/}
      {/*  </div>*/}
      {/*</Card>*/}
    </Wrapper>
  );
};
